import { RenderCellWithEditWrapperProps } from 'ui-component/DataGrid/Render/RenderCellWithEditWrapper';
import { Box, Stack, Typography } from '@mui/material';
import SegmentIconButton from 'ui-component/Segment/SegmentIconButton';
import { IconPencil } from '@tabler/icons-react';
import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { useState } from 'react';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';
import usePermissionStateAndCopyHook from 'hooks/usePermissionStateAndCopyHook';
import { useTheme } from '@mui/material/styles';
import config from 'config';

const CellWithEditWrapper = ({
  params,
  renderMethod,
  permissionScope,
  disabled,
  getIsDisabled,
  tooltip,
  icon,
}: RenderCellWithEditWrapperProps) => {
  const tempClickOffInlineEdit = useFeature(
    FeatureFlags.tempClickOffInlineEdit
  ).on;
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  const { permissionDisabled } = usePermissionStateAndCopyHook({
    permissionScope,
  });

  const handleStartEdit = ({
    id,
    field,
  }: {
    id: GridRenderCellParams['id'];
    field: GridRenderCellParams['field'];
  }) => {
    if (params.isEditable) {
      params.api.startCellEditMode({ id, field });
    }
  };

  const disabledResult = getIsDisabled ? getIsDisabled(params) : false;

  const Icon = icon ?? IconPencil;

  const isEditable =
    params.isEditable && !permissionDisabled && !disabledResult;

  if (tempClickOffInlineEdit)
    return (
      <Stack
        direction={params.colDef.type === 'number' ? 'row-reverse' : 'row'}
        justifyContent={
          params.colDef.type === 'boolean' ? 'center' : 'flex-start'
        }
        alignItems="center"
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          ...(isHovered &&
            isEditable && {
              border: `1px solid ${theme.palette.primary.main}`,
              borderRadius: `${config.borderRadius}px`,
            }),
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        spacing={0}
      >
        <Box overflow="hidden">
          {renderMethod ? (
            <>{renderMethod(params)}</>
          ) : (
            <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {params.formattedValue ?? params.value}
            </Typography>
          )}
        </Box>
        {isHovered && isEditable && (
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            <Icon
              stroke="1.5px"
              width={15}
              height={15}
              color={theme.palette.primary.main}
            />
          </Box>
        )}
      </Stack>
    );

  return (
    <Stack
      direction={params.colDef.type === 'number' ? 'row-reverse' : 'row'}
      justifyContent={
        params.colDef.type === 'boolean' ? 'center' : 'flex-start'
      }
      alignItems="center"
      sx={{ width: '100%', height: '100%' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      spacing={0}
    >
      <Box overflow="hidden">
        {renderMethod ? (
          <>{renderMethod(params)}</>
        ) : (
          <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {params.formattedValue ?? params.value}
          </Typography>
        )}
      </Box>
      <Box>
        <SegmentIconButton
          color="primary"
          onClick={() =>
            handleStartEdit({ id: params.id, field: params.field })
          }
          size="small"
          permissionScope={permissionScope}
          sx={{
            visibility: isHovered && params.isEditable ? 'visible' : 'hidden',
          }}
          disabled={disabled || disabledResult}
          tooltip={tooltip}
        >
          <Icon stroke="1.5px" width={20} height={20} />
        </SegmentIconButton>
      </Box>
    </Stack>
  );
};

export default CellWithEditWrapper;
